import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { 
  Upload, 
  Download, 
  Settings, 
  RefreshCw, 
  FileSpreadsheet,
  AlertTriangle,
  CheckCircle,
  Clock
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLATFORMS = [
  { id: 'amazon_uae', name: 'Amazon UAE' },
  { id: 'amazon_ksa', name: 'Amazon KSA' },
  { id: 'amazon_au', name: 'Amazon AU' },
  { id: 'amazon_ind', name: 'Amazon India' },
  { id: 'noon_uae', name: 'Noon UAE' },
];

export default function DealRecommendationsPage() {
  const [platform, setPlatform] = useState('amazon_ksa');
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState({
    aging_threshold_days: 180,
    no_sales_period_days: 90,
    velocity_threshold: 0,
    use_velocity: false
  });
  const [showSettings, setShowSettings] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const { toast } = useToast();

  const fetchRecommendations = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/deals/recommendations/${platform}`);
      setRecommendations(response.data);
    } catch (error) {
      if (error.response?.status !== 404) {
        toast({
          title: 'Error',
          description: 'Failed to load deal recommendations',
          variant: 'destructive',
        });
      }
      setRecommendations([]);
    } finally {
      setLoading(false);
    }
  }, [platform, toast]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/deals/stats/${platform}`);
      setStats(response.data);
    } catch (error) {
      setStats(null);
    }
  }, [platform]);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/deals/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to load settings');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  useEffect(() => {
    fetchStats();
    fetchRecommendations();
  }, [platform, fetchStats, fetchRecommendations]);

  const handleInventoryAgeUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/deals/upload-inventory-age/${platform}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      toast({
        title: 'Upload Complete',
        description: `${response.data.records_count} inventory age records uploaded`,
      });
      await fetchStats();
      await fetchRecommendations();
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: 'Upload Failed',
        description: error.response?.data?.detail || error.message || 'Failed to upload inventory age file',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handlePricingUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/deals/upload-pricing/${platform}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      toast({
        title: 'Upload Complete',
        description: `${response.data.records_count} pricing records uploaded`,
      });
      await fetchStats();
      await fetchRecommendations();
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: 'Upload Failed',
        description: error.response?.data?.detail || error.message || 'Failed to upload pricing file',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleSaveSettings = async () => {
    try {
      const formData = new FormData();
      formData.append('aging_threshold_days', settings.aging_threshold_days);
      formData.append('no_sales_period_days', settings.no_sales_period_days);
      formData.append('velocity_threshold', settings.velocity_threshold);
      formData.append('use_velocity', settings.use_velocity);

      await axios.put(`${API}/deals/settings`, formData);
      toast({
        title: 'Settings Saved',
        description: 'Deal recommendation settings updated successfully',
      });
      setShowSettings(false);
      fetchRecommendations();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save settings',
        variant: 'destructive',
      });
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}/deals/export/${platform}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `deal_recommendations_${platform}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast({
        title: 'Export Failed',
        description: 'No recommendations to export',
        variant: 'destructive',
      });
    }
  };

  const handleDownloadTemplate = async (type) => {
    try {
      const response = await axios.get(`${API}/deals/download-template/${type}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_template.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast({
        title: 'Download Failed',
        description: 'Failed to download template',
        variant: 'destructive',
      });
    }
  };

  const filteredRecommendations = recommendations.filter(rec =>
    rec.sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
    rec.product_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getAgeBucketColor = (bucket) => {
    switch (bucket) {
      case '365+': return 'bg-red-500/10 text-red-600 border-red-500/20';
      case '271-365': return 'bg-orange-500/10 text-orange-600 border-orange-500/20';
      case '181-270': return 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20';
      case '91-180': return 'bg-blue-500/10 text-blue-600 border-blue-500/20';
      default: return 'bg-green-500/10 text-green-600 border-green-500/20';
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight font-heading" data-testid="page-title">
              Deal Recommendations
            </h1>
            <p className="text-muted-foreground mt-1">
              Generate deal prices for slow-moving inventory
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowSettings(!showSettings)}
              data-testid="settings-btn"
            >
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchRecommendations}
              disabled={loading}
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Settings Panel */}
        {showSettings && (
          <div className="bg-card border border-border rounded-lg p-6" data-testid="settings-panel">
            <h3 className="text-lg font-semibold mb-4">Deal Recommendation Settings</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <Label htmlFor="aging_threshold">Aging Threshold (Days)</Label>
                <Input
                  id="aging_threshold"
                  type="number"
                  value={settings.aging_threshold_days}
                  onChange={(e) => setSettings({...settings, aging_threshold_days: parseInt(e.target.value) || 0})}
                  className="mt-1"
                />
                <p className="text-xs text-muted-foreground mt-1">Products older than this get minimum price</p>
              </div>
              <div>
                <Label htmlFor="no_sales_period">No Sales Period (Days)</Label>
                <Input
                  id="no_sales_period"
                  type="number"
                  value={settings.no_sales_period_days}
                  onChange={(e) => setSettings({...settings, no_sales_period_days: parseInt(e.target.value) || 0})}
                  className="mt-1"
                />
                <p className="text-xs text-muted-foreground mt-1">Products with no sales in this period qualify</p>
              </div>
              <div>
                <Label htmlFor="velocity_threshold">Velocity Threshold (Optional)</Label>
                <Input
                  id="velocity_threshold"
                  type="number"
                  step="0.01"
                  value={settings.velocity_threshold}
                  onChange={(e) => setSettings({...settings, velocity_threshold: parseFloat(e.target.value) || 0})}
                  className="mt-1"
                />
                <p className="text-xs text-muted-foreground mt-1">Use velocity instead of zero sales</p>
              </div>
              <div className="flex items-end">
                <Button onClick={handleSaveSettings} className="w-full" data-testid="save-settings-btn">
                  Save Settings
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Platform Selection & Upload Section */}
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Platform Selector */}
            <div>
              <Label htmlFor="platform">Platform</Label>
              <select
                id="platform"
                value={platform}
                onChange={(e) => setPlatform(e.target.value)}
                className="w-full mt-1 p-2 border border-border rounded-md bg-background"
                data-testid="platform-select"
              >
                {PLATFORMS.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
              
              {/* Stats */}
              {stats && (
                <div className="mt-4 space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    {stats.inventory_age_records > 0 ? (
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    ) : (
                      <AlertTriangle className="w-4 h-4 text-yellow-500" />
                    )}
                    <span>Inventory Age: {stats.inventory_age_records} records</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {stats.pricing_records > 0 ? (
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    ) : (
                      <AlertTriangle className="w-4 h-4 text-yellow-500" />
                    )}
                    <span>Pricing: {stats.pricing_records} records</span>
                  </div>
                  {stats.last_inventory_update && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Clock className="w-3 h-3" />
                      <span className="text-xs">
                        Last update: {new Date(stats.last_inventory_update).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Inventory Age Upload */}
            <div>
              <Label>Upload Inventory Age</Label>
              <div className="mt-1 space-y-2">
                <label className="flex items-center justify-center w-full h-20 border-2 border-dashed border-border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
                  <div className="text-center">
                    <Upload className="w-6 h-6 mx-auto text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      {uploading ? 'Uploading...' : 'Click to upload'}
                    </span>
                  </div>
                  <input
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={handleInventoryAgeUpload}
                    className="hidden"
                    disabled={uploading}
                    data-testid="inventory-age-upload"
                  />
                </label>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full text-xs"
                  onClick={() => handleDownloadTemplate('inventory-age')}
                  data-testid="download-inventory-template"
                >
                  <FileSpreadsheet className="w-3 h-3 mr-1" />
                  Download Template
                </Button>
              </div>
            </div>

            {/* Pricing Upload */}
            <div>
              <Label>Upload Pricing Sheet</Label>
              <div className="mt-1 space-y-2">
                <label className="flex items-center justify-center w-full h-20 border-2 border-dashed border-border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
                  <div className="text-center">
                    <Upload className="w-6 h-6 mx-auto text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      {uploading ? 'Uploading...' : 'Click to upload'}
                    </span>
                  </div>
                  <input
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={handlePricingUpload}
                    className="hidden"
                    disabled={uploading}
                    data-testid="pricing-upload"
                  />
                </label>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full text-xs"
                  onClick={() => handleDownloadTemplate('pricing')}
                  data-testid="download-pricing-template"
                >
                  <FileSpreadsheet className="w-3 h-3 mr-1" />
                  Download Template
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Recommendations Table */}
        <div className="bg-card border border-border rounded-lg">
          <div className="p-4 border-b border-border flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <h3 className="text-lg font-semibold">Deal Recommendations</h3>
              <span className="text-sm text-muted-foreground">
                {filteredRecommendations.length} products
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Input
                placeholder="Search SKU or product..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-64"
                data-testid="search-input"
              />
              <Button
                onClick={handleExport}
                disabled={recommendations.length === 0}
                data-testid="export-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="p-8 text-center text-muted-foreground">
              Loading recommendations...
            </div>
          ) : filteredRecommendations.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              {recommendations.length === 0 
                ? 'No deal recommendations. Upload inventory age data to get started.'
                : 'No results match your search.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="p-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">SKU</th>
                    <th className="p-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Product Name</th>
                    <th className="p-3 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider">Age Bucket</th>
                    <th className="p-3 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider">Qty</th>
                    <th className="p-3 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider">Sales (90d)</th>
                    <th className="p-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">Deal Price / Action</th>
                    <th className="p-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Reason</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredRecommendations.map((rec, idx) => (
                    <tr key={`${rec.sku}-${idx}`} className="hover:bg-muted/30">
                      <td className="p-3 text-sm font-mono">{rec.sku}</td>
                      <td className="p-3 text-sm max-w-[200px] truncate" title={rec.product_name}>
                        {rec.product_name}
                      </td>
                      <td className="p-3 text-center">
                        <span className={`px-2 py-1 text-xs font-medium rounded border ${getAgeBucketColor(rec.age_bucket)}`}>
                          {rec.age_bucket}
                        </span>
                      </td>
                      <td className="p-3 text-center text-sm">{rec.total_aged_quantity}</td>
                      <td className="p-3 text-center text-sm">{rec.sales_last_90_days}</td>
                      <td className="p-3 text-right text-sm font-semibold">
                        {rec.deal_price != null ? (
                          rec.deal_price.toFixed(2)
                        ) : (
                          <span className="text-orange-600 font-medium">{rec.price_action}</span>
                        )}
                      </td>
                      <td className="p-3 text-sm text-muted-foreground">{rec.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
